import { useCallback, useEffect, useState } from "react";
import { Camera, Plus, Trash2, Loader2, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { resizeImageForEmail } from "@/lib/imageResize";

// Mesmo upload (/api/upload) e o mesmo redimensionamento das fotografias de atualizações.
async function uploadPhotos(fileList, onEach) {
    const list = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    const ids = [];
    for (const file of list) {
        let payload = file;
        try {
            payload = await resizeImageForEmail(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.82 });
        } catch { /* fall back to raw file */ }
        try {
            const fd = new FormData();
            fd.append("file", payload);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            ids.push(data.id);
            onEach && onEach(data.id);
        } catch {
            toast.error(`Falha ao carregar ${file.name}`);
        }
    }
    return ids;
}

function AddPhotosButton({ busy, onFiles, testid, label = "Adicionar fotografias" }) {
    return (
        <label className={`w-full md:w-auto inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-800 transition-colors ${busy ? "opacity-50 pointer-events-none" : "cursor-pointer hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))]"}`} data-testid={testid}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {busy ? "A carregar…" : label}
            <input type="file" accept="image/*" multiple className="hidden" disabled={busy}
                onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
        </label>
    );
}

function Thumb({ id, isImage = true, onRemove, removeLabel, testid }) {
    return (
        <div className="relative" data-testid={testid}>
            <a href={fileUrl(id)} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-stone-100 hover:ring-2 hover:ring-[hsl(var(--copper))]" data-testid={`${testid}-open`}>
                {isImage ? (
                    <img src={fileUrl(id)} alt="Fotografia do pedido" loading="lazy" className="w-full h-full object-cover" />
                ) : (
                    <span className="w-full h-full flex flex-col items-center justify-center gap-1 text-xs text-stone-600"><FileText className="w-5 h-5" /> Ver anexo</span>
                )}
            </a>
            {onRemove && (
                <button type="button" onClick={onRemove} aria-label={removeLabel}
                    className="absolute top-1 right-1 w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-red-700 hover:bg-white"
                    data-testid={`${testid}-delete`}>
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

/** Seletor de fotografias para o formulário de criação manual. */
export function NewLeadPhotoPicker({ ids, onChange, onBusy }) {
    const [busy, setBusy] = useState(false);
    const add = async (files) => {
        setBusy(true); onBusy(true);
        const added = await uploadPhotos(files);
        onChange((prev) => [...prev, ...added]);
        setBusy(false); onBusy(false);
    };
    return (
        <div data-testid="ln-photos">
            <span className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Fotografias</span>
            {ids.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                    {ids.map((id) => (
                        <div key={id} className="relative" data-testid={`ln-photo-${id}`}>
                            <img src={fileUrl(id)} alt="Fotografia do pedido" className="w-full aspect-square object-cover rounded-lg bg-stone-100" />
                            <button type="button" onClick={() => onChange((prev) => prev.filter((x) => x !== id))} aria-label="Remover fotografia"
                                className="absolute top-1 right-1 w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center" data-testid={`ln-photo-remove-${id}`}>
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <AddPhotosButton busy={busy} onFiles={add} testid="ln-photos-add" />
        </div>
    );
}

/** Secção "Fotografias do pedido" na ficha (cliente pelo site + Backoffice). */
export function LeadPhotosSection({ leadId, onChanged }) {
    const [photos, setPhotos] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/leads/${leadId}/photos`);
            setPhotos(data);
        } catch {
            setPhotos([]);
        }
    }, [leadId]);

    useEffect(() => { setPhotos(null); load(); }, [load]);

    const add = async (files) => {
        setBusy(true);
        try {
            const ids = await uploadPhotos(files);
            if (!ids.length) return;
            await api.post(`/admin/leads/${leadId}/photos`, { file_ids: ids });
            toast.success(ids.length === 1 ? "Fotografia adicionada." : `${ids.length} fotografias adicionadas.`);
            await load();
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao adicionar fotografias");
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminar esta fotografia do pedido?")) return;
        try {
            await api.delete(`/admin/leads/${leadId}/photos/${id}`);
            setPhotos((list) => list.filter((p) => p.id !== id));
            toast.success("Fotografia eliminada.");
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao eliminar fotografia");
        }
    };

    return (
        <div data-testid="lead-photos">
            <div className="flex items-center gap-2 mb-2">
                <Camera className="w-4 h-4 text-stone-500" />
                <span className="text-xs uppercase tracking-wider text-stone-500 font-medium">Fotografias do pedido</span>
                {photos && photos.length > 0 && <span className="text-xs text-stone-400" data-testid="lead-photos-count">· {photos.length}</span>}
            </div>
            {photos === null ? (
                <div className="text-sm text-stone-500 flex items-center gap-2 mb-3"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>
            ) : photos.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3" data-testid="lead-photos-grid">
                    {photos.map((p) => (
                        <Thumb key={p.id} id={p.id} isImage={(p.content_type || "image/").startsWith("image/")}
                            onRemove={p.deletable ? () => remove(p.id) : null} removeLabel="Eliminar fotografia"
                            testid={`lead-photo-${p.id}`} />
                    ))}
                </div>
            ) : (
                <p className="text-xs text-stone-500 mb-3" data-testid="lead-photos-empty">Ainda não há fotografias neste pedido.</p>
            )}
            <AddPhotosButton busy={busy} onFiles={add} testid="lead-photos-add" />
        </div>
    );
}
